import { ReactNode, useCallback, useMemo } from 'react';
import Button from '../../Button';
import { ArrowLeftIcon } from '../../Icons';
import { CheckoutState, OriginString } from '@shared/types';
import { ResponseSubmitter } from '../useCheckout';
import { Breadcrumbs } from '../../Breadcrumbs';
import styles from './styles.module.scss';
import { useIam } from '../../../services/IAm/hooks';

export type CheckoutBreadcrumbsProps<TCheckoutState extends CheckoutState> = {
    unauthenticatedSteps: Array<{
        key: TCheckoutState['currentStep'];
        label: string;
    }>;
    authenticatedSteps: Array<{
        key: TCheckoutState['currentStep'];
        label: string;
    }>;
    currentStep: TCheckoutState['currentStep'];
    submitResponse: ResponseSubmitter<TCheckoutState>;
    origin: OriginString;
    educatorSlug: string;
    courseSlug: string;
};

const loginStep = {
    key: '_logInOrSignUp',
    label: 'Log in or Sign Up',
};

const getEducatorLink = (origin: string, educatorSlug: string): string => {
    if (origin === 'embed' || origin === 'landing_page') {
        return `https://${educatorSlug}.${process.env.NEXT_PUBLIC_DOMAIN_NAME}`;
    }

    return `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educatorSlug}`;
};

export const CheckoutBreadcrumbs = <TCheckoutState extends CheckoutState>({
    unauthenticatedSteps,
    authenticatedSteps,
    currentStep,
    submitResponse,
    origin,
    educatorSlug,
    courseSlug,
}: CheckoutBreadcrumbsProps<TCheckoutState>): ReactNode => {
    const { iam } = useIam();

    const educatorLink: string = useMemo(
        () => getEducatorLink(origin, educatorSlug),
        [educatorSlug, origin]
    );

    const steps = useMemo(() => {
        if (iam.authenticated === true) {
            return [...unauthenticatedSteps, ...authenticatedSteps];
        }
        return [...unauthenticatedSteps, loginStep, ...authenticatedSteps];
    }, [iam, unauthenticatedSteps, authenticatedSteps]);

    const isOnAuthenticatedStep = useMemo(() => {
        return authenticatedSteps.some((authenticatedStep) => {
            return authenticatedStep.key === currentStep;
        });
    }, [authenticatedSteps, currentStep]);

    const currentStepIndex = useMemo(() => {
        if (iam.authenticated === false && isOnAuthenticatedStep) {
            return steps.findIndex((step) => {
                return step.key === loginStep.key;
            });
        }
        return steps.findIndex((step) => {
            return step.key === currentStep;
        });
    }, [iam, isOnAuthenticatedStep, steps, currentStep]);

    const onGoBackClick = useCallback(() => {
        if (origin === 'embed' && currentStepIndex === 0) {
            window.location.href = `${educatorLink}/${courseSlug}`;
        }
        if (currentStepIndex === 0) {
            return history.back();
        }
        return submitResponse({ actionType: 'go back', payload: {} });
    }, [courseSlug, currentStepIndex, educatorLink, origin, submitResponse]);

    return (
        <div className={styles.container}>
            <Button preset="textButton" className={styles.backButton} onClick={onGoBackClick}>
                <ArrowLeftIcon /> Back
            </Button>
            <span className={styles.angleRight}>&rsaquo;</span>
            <Breadcrumbs variant="step" currentIndex={currentStepIndex}>
                {steps.map((step, index) => {
                    return (
                        <span key={step.key}>
                            {index + 1}. {step.label}
                        </span>
                    );
                })}
            </Breadcrumbs>
        </div>
    );
};
